/**
 * Rules Manager: фоновые Apply / Stop rules — общий трекер заданий.
 *
 * Модалка заводит задание и закрывается; дальше задание живёт здесь, а не
 * в странице: тостер с итогом должен прийти, даже если человек ушёл на
 * другую страницу. Номера незаконченных заданий лежат в localStorage — после
 * перезагрузки вкладка подхватывает их вместе со списком активных с сервера.
 *
 * Тексты (`pending_text`, `message`, `details`) собирает сервер.
 */

import { useSyncExternalStore } from "react";

export type RulesBulkJobStatus =
  | "queued"
  | "running"
  | "completed"
  | "partially_failed"
  | "failed";

export type RulesBulkJobIssue = {
  rule_id: number | null;
  rule_name: string | null;
  reason: string;
};

export type RulesBulkJob = {
  job_id: number;
  status: RulesBulkJobStatus;
  action: string;
  entity_type: string;
  rule_count: number;
  pending_text: string;
  message: string | null;
  details: RulesBulkJobIssue[];
};

export type ToastKind = "info" | "success" | "warning" | "error";

export type JobToast = {
  key: string;
  kind: ToastKind;
  text: string;
  details: RulesBulkJobIssue[];
  /** Сам не исчезает — только крестиком. */
  sticky: boolean;
  jobId: number | null;
};

const STORAGE_KEY = "oneads.rules_bulk_jobs";
const TERMINAL: RulesBulkJobStatus[] = ["completed", "partially_failed", "failed"];

export function isTerminal(status: RulesBulkJobStatus): boolean {
  return TERMINAL.includes(status);
}

/** Тостер по заданию: идёт → «Applying 3 rules…»; итог — по статусу.
 *  Ошибка и частичный успех не исчезают сами: в них причины, которые
 *  человек должен успеть прочесть. */
export function toastFor(job: RulesBulkJob): JobToast {
  const key = `job-${job.job_id}`;
  if (!isTerminal(job.status)) {
    return { key, kind: "info", text: job.pending_text, details: [], sticky: true, jobId: job.job_id };
  }
  if (job.status === "completed") {
    return { key, kind: "success", text: job.message ?? "Done", details: [], sticky: false, jobId: job.job_id };
  }
  if (job.status === "partially_failed") {
    return { key, kind: "warning", text: job.message ?? "Done with exceptions", details: job.details, sticky: true, jobId: job.job_id };
  }
  return {
    key,
    kind: "error",
    text: job.message ?? `Failed to ${job.action === "stop" ? "stop" : "apply"} rules`,
    details: job.details,
    sticky: true,
    jobId: job.job_id,
  };
}

// ---------------------------------------------------------------- хранилище

const listeners = new Set<() => void>();
let toasts: JobToast[] = [];
let counter = 0;

function emit(): void {
  toasts = [...toasts];
  listeners.forEach((l) => l());
}

export function useJobToasts(): JobToast[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => toasts,
    () => toasts,
  );
}

export function upsertToast(t: JobToast): void {
  const i = toasts.findIndex((x) => x.key === t.key);
  if (i >= 0) toasts[i] = t;
  else toasts.push(t);
  emit();
}

export function dismissToast(key: string): void {
  const before = toasts.length;
  toasts = toasts.filter((t) => t.key !== key);
  if (toasts.length !== before) emit();
}

export function notice(text: string, kind: ToastKind = "info"): string {
  counter += 1;
  const key = `notice-${counter}`;
  upsertToast({ key, kind, text, details: [], sticky: false, jobId: null });
  return key;
}

// ------------------------------------------------- незаконченные задания (LS)

function readIds(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(ids) ? ids.filter((x): x is number => Number.isInteger(x)) : [];
  } catch {
    return [];
  }
}

function writeIds(ids: number[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch {
    /* приватный режим и т. п. — трекинг продолжится в памяти */
  }
}

export function trackedIds(): number[] {
  return readIds();
}

export function rememberJob(id: number): void {
  writeIds([...readIds(), id]);
}

export function forgetJob(id: number): void {
  writeIds(readIds().filter((x) => x !== id));
}

/** Новое задание из модалки: тостер «идёт» сразу, без ожидания опроса. */
export function trackJob(job: RulesBulkJob): void {
  rememberJob(job.job_id);
  upsertToast(toastFor(job));
}

/** Для тестов. */
export function _resetForTests(): void {
  toasts = [];
  counter = 0;
}
